import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Instagram, Sparkles, Loader2, Copy } from "lucide-react";

export default function SocialPostGenerator() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [product, setProduct] = useState("");
  const [tone, setTone] = useState("warm");
  const [platform, setPlatform] = useState("instagram");
  const [highlight, setHighlight] = useState("");
  const [busy, setBusy] = useState(false);
  const [posts, setPosts] = useState([]);

  useEffect(() => { if (!loading && (!user || user === false)) nav("/login"); }, [user, loading, nav]);

  const gen = async () => {
    if (product.trim().length < 2) { toast.error("What are you posting about?"); return; }
    setBusy(true); setPosts([]);
    try {
      const { data } = await api.post("/marketing/social-post", { product, tone, platform, highlight }, { timeout: 60000 });
      setPosts(data.posts || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const copy = (p) => {
    const txt = `${p.caption}\n\n${(p.hashtags || []).join(" ")}\n${p.cta || ""}`;
    try {
      navigator.clipboard.writeText(txt).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed"));
    } catch { toast.error("Copy failed"); }
  };

  if (loading || !user) return null;
  return (
    <>
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><Instagram className="w-3.5 h-3.5" style={{ color: "#E1306C" }} /><span>Marketing tool</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">Social Media <span style={{ color: "#E1306C" }}>Post</span> Generator</h1>
          <p className="mt-2 text-[#857163]">Get 3 ready-to-post captions with hashtags and CTAs for your bakes.</p>
        </div>
        <div className="cp-card p-5 mb-6 space-y-3">
          <label><span className="text-xs text-[#857163]">What are you posting about?</span><input className="cp-input mt-1" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Fresh chocolate sourdough available this weekend" data-testid="product" /></label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label><span className="text-xs text-[#857163]">Platform</span><select className="cp-input mt-1" value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="platform">
              <option value="instagram">Instagram</option><option value="facebook">Facebook</option><option value="tiktok">TikTok</option><option value="twitter">X / Twitter</option>
            </select></label>
            <label><span className="text-xs text-[#857163]">Tone</span><select className="cp-input mt-1" value={tone} onChange={(e) => setTone(e.target.value)} data-testid="tone">
              <option value="warm">Warm & personal</option><option value="playful">Playful</option><option value="professional">Professional</option><option value="urgent">Urgent / limited</option><option value="lifestyle">Lifestyle</option>
            </select></label>
          </div>
          <label><span className="text-xs text-[#857163]">Highlight or hook (optional)</span><input className="cp-input mt-1" value={highlight} onChange={(e) => setHighlight(e.target.value)} placeholder="Only 12 loaves this weekend, order by Friday" data-testid="highlight" /></label>
          <button onClick={gen} disabled={busy || !product.trim()} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-semibold disabled:opacity-50" style={{ background: "linear-gradient(135deg, #E1306C, #C13584)" }} data-testid="gen-btn">
            {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Writing…</> : <><Sparkles className="w-5 h-5" /> Generate posts</>}
          </button>
        </div>
        {posts.length > 0 && (
          <div className="space-y-3" data-testid="post-results">
            {posts.map((p, i) => (
              <div key={i} className="cp-card p-5" data-testid={`post-${i}`}>
                <div className="flex items-center justify-between mb-2"><div className="text-xs uppercase tracking-widest text-[#857163]">Variation {i + 1}</div><button onClick={() => copy(p)} className="text-xs inline-flex items-center gap-1 px-3 py-1 rounded-full text-white" style={{ background: "#E1306C" }} data-testid={`copy-${i}`}><Copy className="w-3 h-3" /> Copy</button></div>
                <div className="whitespace-pre-wrap text-sm text-[#3B2C24]">{p.caption}</div>
                {p.hashtags?.length > 0 && <div className="mt-2 text-sm text-[#2F80ED]">{p.hashtags.join(" ")}</div>}
                {p.cta && <div className="mt-2 text-sm font-semibold text-[#3B2C24]">→ {p.cta}</div>}
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
}
